import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightLeft, Copy, AlertCircle, Check } from 'lucide-react';

export const M3UToXCConverter = () => {
    const [m3uUrl, setM3uUrl] = useState('');
    const [output, setOutput] = useState(null);
    const [copiedField, setCopiedField] = useState(null);

    const convert = () => {
        try {
            const url = new URL(m3uUrl);
            const params = new URLSearchParams(url.search);

            // Extract from query params usually found in M3U Plus URLs
            const username = params.get('username');
            const password = params.get('password');
            const server = url.origin;

            if (!username || !password) {
                setOutput({ error: 'Could not detect username/password in URL. Ensure it is a standard M3U_PLUS link.' });
                return;
            }

            setOutput({
                server,
                username,
                password,
                // Construct standard Xtream Codes 1-line Player API
                playerApiUrl: `${server}/player_api.php?username=${username}&password=${password}`
            });
        } catch (e) {
            setOutput({ error: 'Invalid URL format. Please enter a valid URL (e.g., http://host.com/get.php?...).' });
        }
    };

    const copyToClipboard = (text, field) => {
        navigator.clipboard.writeText(text);
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 2000);
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
                <div className="p-4 bg-primary/10 rounded-2xl border border-primary/20 shadow-[0_0_15px_rgba(var(--primary-rgb),0.2)]">
                    <ArrowRightLeft className="w-8 h-8 text-primary" />
                </div>
                <div>
                    <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-white/60">M3U → Xtream Codes</h2>
                    <p className="text-muted-foreground">Extract server, username, and password from your M3U link.</p>
                </div>
            </div>

            <div className="glass p-6 rounded-2xl border border-white/10 space-y-4">
                <div>
                    <label className="block text-sm font-medium mb-2 text-gray-300">Paste M3U URL</label>
                    <div className="relative">
                        <input
                            type="text"
                            value={m3uUrl}
                            onChange={(e) => setM3uUrl(e.target.value)}
                            placeholder="http://domain.com/get.php?username=XXX&password=XXX&type=m3u_plus"
                            className="w-full px-4 py-4 bg-black/40 border border-white/10 rounded-xl focus:border-primary/50 focus:ring-1 focus:ring-primary/50 outline-none transition-all font-mono text-sm"
                        />
                    </div>
                </div>

                <button
                    onClick={convert}
                    disabled={!m3uUrl}
                    className="w-full py-4 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-bold text-lg transition-all shadow-lg shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Extract Credentials
                </button>
            </div>

            {output && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`rounded-2xl border p-6 ${output.error ? 'bg-red-500/10 border-red-500/30' : 'glass border-white/10'}`}
                >
                    {output.error ? (
                        <div className="flex items-center gap-3 text-red-400">
                            <AlertCircle className="w-6 h-6" />
                            <p>{output.error}</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {[
                                { label: 'Server URL', value: output.server, key: 'server' },
                                { label: 'Username', value: output.username, key: 'username' },
                                { label: 'Password', value: output.password, key: 'password' },
                                { label: 'Player API Link', value: output.playerApiUrl, key: 'api' }
                            ].map((item) => (
                                <div key={item.key} className="group">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{item.label}</span>
                                        <button
                                            onClick={() => copyToClipboard(item.value, item.key)}
                                            className="text-primary hover:text-white transition-colors"
                                        >
                                            {copiedField === item.key ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                        </button>
                                    </div>
                                    <div className="bg-black/40 p-3 rounded-lg border border-white/5 font-mono text-sm text-green-400 break-all select-all">
                                        {item.value}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </motion.div>
            )}
        </div>
    );
};
